import { Injectable } from '@angular/core';
import { Observable, delay, finalize, take } from 'rxjs';
import { FeUtil, UiService, ENV, TYPE, API } from '../../../hezky-routes';
import { REPORTS, SCHEDULER } from '../hezky-fe-services/api.service';
import { selectReportGenerateDate } from '../../../modules/reports/state/report.state';
import { loadReportData, loadReportFilter, loadReportFilterMetaData } from '../../../modules/reports/state/report.actions';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GenericConfirmationDialogComponent } from '../../../shared/components/generic-confirmation-dialog/generic-confirmation-dialog.component';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { DateTime } from 'luxon';
import { NavigationExtras } from '@angular/router';
import { NzCheckBoxOptionInterface } from 'ng-zorro-antd/checkbox';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class ReportService {
  public isViewReport: boolean = false;
  public isDownload: boolean = false;
  auditOptions: NzCheckBoxOptionInterface[] = [];
  incidentLengthOptions: NzCheckBoxOptionInterface[] = [
    { label: 'Less than 1 hour', value: '1' },
    { label: 'Between 1/3 hours', value: '3' },
    { label: 'Between 3/6 hours', value: '6' },
    { label: 'More than 6 hours', value: '7' },
  ]
  incidentStatusOptions: NzCheckBoxOptionInterface[] = [
    { label: 'Active', value: 'Active' },
    { label: 'Closed', value: 'Closed' },
  ]
  scheduleOptions: NzCheckBoxOptionInterface[] = [
    { label: 'One Time', value: 'one time' },
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' },
  ];
  severityOptions: NzCheckBoxOptionInterface[] = [
    { label: 'Low', value: 'Low' },
    { label: 'Medium', value: 'Medium' },
    { label: 'High', value: 'High' },
  ]
  delayOptions: NzCheckBoxOptionInterface[] = [
    { label: 'Less than 2 hours', value: 'less_than_2_hours' },
    { label: '2 to 6 hours', value: '2_to_6_hours' },
    { label: '6 to 12 hours', value: '6_to_12_hours' },
    { label: '12 to 24 hours', value: '12_to_24_hours' },
    { label: 'More than 24 hours', value: 'more_than_24_hours' }
  ]
  durationOptions: NzCheckBoxOptionInterface[] = [
    { label: 'Less than 1 hour', value: '1' },
    { label: 'Between 1/3 hours', value: '3' },
    { label: 'Between 3/6 hours', value: '6' },
    { label: 'More than 6 hours', value: '7' },
  ]

  private loadDefaults(): void {
    this.feUtil.getDisplayProduct.pipe(untilDestroyed(this)).subscribe((product: TYPE.Application | null) => {
      const options: { [key: string]: NzCheckBoxOptionInterface[] } = {
        storage: [
          { label: 'Incident', value: 'Incident' },
          { label: 'Account', value: 'Account' },
          { label: 'Asset', value: 'Asset' },
        ],
        transport: [
          { label: 'Incident', value: 'Incident' },
          { label: 'Account', value: 'Account' },
          { label: 'Trip', value: 'Trip' },
        ]
      };
      const applicationName = product?.name ?? '';
      this.auditOptions = options[applicationName as keyof typeof options] || [];
    });
  }

  // * API CALL METHODS
  fetchReports(payload: any): Observable<any> {
    return this.feUtil.httpSvc.get(REPORTS.GET_REPORTS, payload)
  }

  fetchScheduleReports(payload: any): Observable<any> {
    return this.feUtil.httpSvc.get(SCHEDULER.GET_REPORT, payload)
  }

  fetchTripSummaryReport(payload: any): Observable<any> {
    return this.feUtil.httpSvc.post(REPORTS.TRIP_SUMMARY_REPORT, payload)
  }

  signAndSubmitReport(payload: any): Observable<any> {
    return this.feUtil.httpSvc.post(REPORTS.SIGN_AND_SUBMIT_REPORT, payload)
  }

  // * DASHBOARD API CALLS
  fetchDashboardData(payload: any): Observable<any> {
    return this.feUtil.httpSvc.get(REPORTS.GET_DASHBOARD_DATA, payload)
  }

  // * REPORTS METHODS
  public viewReportHandler(item: any) {
    this.isViewReport = true;
    let reportType = item['report_name'].split('-')[0];
    let payload = item?.filters;
    if (item?.filters) {
      payload = {
        ...item?.filters,
        start_date: typeof item?.filters?.start_date === 'number'
          ? DateTime.fromMillis(item?.filters?.start_date).toUTC().toFormat('yyyy-MM-dd HH:mm:ss')
          : item?.filters?.start_date,
        end_date: typeof item?.filters?.end_date === 'number'
          ? DateTime.fromMillis(item?.filters?.end_date).toUTC().toFormat('yyyy-MM-dd HH:mm:ss')
          : item?.filters?.end_date,
      }
      const isIdtAsset = item?.filters?.idt_asset;
      if (isIdtAsset) { payload['asset_ids'] = isIdtAsset }
      this.generateReport(payload, reportType).pipe(untilDestroyed(this), delay(500), finalize(() => this.isViewReport = false)).subscribe({
        next: ({ data, status }) => {
          if (status !== 200) return;
          this.isViewReport = false;
          this.feUtil.store.dispatch(loadReportFilter({ reportFilter: item?.filters }));
          this.feUtil.store.dispatch(loadReportData({ reportData: data }));
          this.feUtil.store.dispatch(loadReportFilterMetaData({ filterMetaData: item }));
          this.routeOnReportType(reportType);
        },
      })
    }
  }

  generateReport(payload: any, reportType: string): Observable<any> {
    let URL: string;
    switch (reportType) {
      case 'Device Report':
      case 'device_report':
        URL = REPORTS.DEVICE_REPORT;
        break;
      case 'Asset Summary':
      case 'asset_summary_report':
        URL = REPORTS.ASSET_REPORT;
        break;
      case 'Daily Summary Report':
      case 'daily_summary_report':
        URL = REPORTS.DAILY_REPORT;
        break;
      case 'Incident Summary Report':
      case 'incident_summary_report':
        URL = REPORTS.INCIDENT_REPORT;
        break;
      case 'Audit Log':
      case 'audit_log_report':
        URL = REPORTS.AUDIT_REPORT;
        break;
      case 'Transporter Report':
      case 'transporter_report':
        URL = REPORTS.TRANSPORTER_REPORT;
        break;
      case 'Trip Summary Report':
      case 'trip_summary_report':
        URL = REPORTS.TRIP_SUMMARY_REPORT;
        break;
      case 'Dispatch Delay Report':
      case 'dispatch_delay_report':
        URL = REPORTS.DISPATCH_DELAY_REPORT;
        break;
      case 'Incident Report':
      case 'transport_incident_report':
        URL = REPORTS.TRANSPORT_INCIDENT_REPORT;
        break;
      default:
        throw new Error(`Unsupported report type: ${reportType}`);
    }
    return this.feUtil.httpSvc.post(URL, payload)
  }

  routeOnReportType(report_type: string) {
    switch (report_type) {
      case 'Device Report':
      case 'device_report':
        this.feUtil.router.navigate([`/reports/device-report`]);
        break;
      case 'Asset Summary':
      case 'asset_summary_report':
        this.feUtil.router.navigate([`/reports/asset-summary`]);
        break;
      case 'Daily Summary Report':
      case 'daily_summary_report':
        this.feUtil.router.navigate([`/reports/daily-report`]);
        break;
      case 'Incident Summary Report':
      case 'incident_summary_report':
        this.feUtil.router.navigate([`/reports/incident-report`]);
        break;
      case 'Audit Log':
      case 'audit_log_report':
        this.feUtil.router.navigate([`/reports/audit-report`]);
        break;
      case 'Trip Summary Report':
      case 'trip_summary_report':
        this.feUtil.router.navigate([`/reports/trip-summary-report`]);
        break;
      case 'Transporter Report':
      case 'transporter_report':
        this.feUtil.router.navigate([`/reports/transporter-report`]);
        break;
      case 'Dispatch Delay Report':
      case 'dispatch_delay_report':
        this.feUtil.router.navigate([`/reports/dispatch-delay-report`]);
        break;
      case 'Incident Report':
      case 'transport_incident_report':
        this.feUtil.router.navigate([`/reports/transport-incident-report`]);
        break;
    }
  }

  public reportEventHandler(action: 'save' | 'sign' | 'submit' | 'download', report_type: string, filter: any, filterMetaData?: any): void {
    this.feUtil.store.select(selectReportGenerateDate).pipe(take(1)).subscribe({
      next: (generateDate) => {
        if (generateDate) {
          if (filterMetaData && Object.getOwnPropertyDescriptor(filterMetaData, 'saved_at')?.writable !== false) {
            filterMetaData['saved_at'] = generateDate;
          } else {
            console.log('saved_at is readonly');
          }
        }
      }
    });
    let payload: any = {
      ...this.feUtil.nLevelPayload,
      user_id: this.feUtil.user_data.user_id,
      report_type: report_type,
      filters: [filter || filterMetaData?.filters],
      customer_id: this.feUtil.user_data.customer.customer_id,
      remarks: filterMetaData?.remarks,
    };
    if (filterMetaData?.view_name) {
      payload['view_name'] = filterMetaData?.view_name;
    }
    const handleResponse = (res: any, successMessage: string) => {
      const { status, data, message } = res;
      const reportName = data?.report_name;
      const reportData = data?.data || {};
      reportData['report_name'] = reportName;
      if(status !== 200) {
        this.feUtil.message.error(message, { nzDuration: 3000 });
        return;
      }
      this.feUtil.message.success(successMessage, { nzDuration: 3000 });
      this.feUtil.store.dispatch(loadReportFilterMetaData({ filterMetaData: { ...reportData } }));
    };
    const signAndSubmitWithModal = (successMessage: string, additionalPayload: any = {}) => {
      this.openPassAndRemarksModal().afterClose.pipe(take(1)).subscribe((value) => {
        if (value?.password && value?.remarks) {
          const finalPayload = { ...payload, ...additionalPayload, password: value.password, remarks: value.remarks };
          this.signAndSubmitObservable(finalPayload).pipe(take(1)).subscribe((res) => handleResponse(res, successMessage));
        }
      });
    };
    const downloadReport = (filterMetaData: any) => {
      let reportType = filterMetaData?.report_name?.split('-')[0];
      const filters = { ...filterMetaData?.filters };
      this.feUtil.message.info('Request in progress you will receive an email soon...', { nzDuration: 2000 });
      const start_date = typeof filters?.start_date === 'number'
        ? DateTime.fromMillis(filters?.start_date).toUTC().toFormat('yyyy-MM-dd HH:mm:ss')
        : filters?.start_date;
      const end_date = typeof filters?.end_date === 'number'
        ? DateTime.fromMillis(filters?.end_date).toUTC().toFormat('yyyy-MM-dd HH:mm:ss')
        : filters?.end_date;

      filters.start_date = start_date;
      filters.end_date = end_date;
      const downloadPayload = {
        on_location: filterMetaData?.filters?.location,
        is_download: true,
        report_name: filterMetaData?.report_name,
        ...filters,
      };
      this.isDownload = true;
      this.generateReport(downloadPayload, report_type)
        .pipe(untilDestroyed(this), finalize(() => this.isDownload = false))
        .subscribe({
          next: ({ status}) => {
            if (status !== 200) return;
            this.feUtil.message.success('The report has been successfully delivered to your registered email address!', { nzDuration: 3000 });
          }
        });
    }
    switch (action) {
      case 'save':
        this.uiSvc.saveReportModal().pipe(take(1)).subscribe((reportName: string) => {
          if (reportName) {
            Object.assign(
              payload, {
              is_saved: true,
              is_submit: false,
              is_signed: false,
              view_name: reportName,
              saved_at: filterMetaData?.saved_at ? DateTime.fromFormat(filterMetaData?.saved_at, "yyyy-MM-dd HH:mm:ss").toUTC().toFormat('yyyy-MM-dd HH:mm:ss') : DateTime.now().toUTC().toFormat('yyyy-MM-dd HH:mm:ss')
            });
            this.signAndSubmitObservable(payload).pipe(take(1)).subscribe((res) => handleResponse(res, 'Report saved successfully!'));
          }
        });
        break;
      case 'sign':
        if (!filterMetaData || Object.keys(filterMetaData).length === 0 || !filterMetaData.is_saved) {
          this.feUtil.message.warning('Please save your report before proceeding with signing.', { nzDuration: 3000 });
          this.uiSvc.saveReportModal().pipe(take(1)).subscribe((reportName: string) => {
            if (reportName) {
              Object.assign(
                payload, {
                is_saved: true,
                is_submit: false,
                is_signed: false,
                view_name: reportName,
                report_name: filterMetaData?.report_name,
                saved_at: filterMetaData?.saved_at ? DateTime.fromFormat(filterMetaData?.saved_at, "yyyy-MM-dd HH:mm:ss").toUTC().toFormat('yyyy-MM-dd HH:mm:ss') : DateTime.now().toUTC().toFormat('yyyy-MM-dd HH:mm:ss')
              });
              this.signAndSubmitObservable(payload).pipe(take(1)).subscribe((response) => handleResponse(response, 'Report saved successfully!'));
            }
          });
        } else {
          signAndSubmitWithModal('The report has been signed successfully!', {
            is_saved: true,
            is_signed: true,
            is_submit: false,
            report_name: filterMetaData?.report_name,
            saved_at: filterMetaData?.saved_at ? DateTime.fromFormat(filterMetaData?.saved_at, "yyyy-MM-dd HH:mm:ss").toUTC().toFormat('yyyy-MM-dd HH:mm:ss') : DateTime.now().toUTC().toFormat('yyyy-MM-dd HH:mm:ss'),
            sign_at: filterMetaData?.signed_at ? DateTime.fromFormat(filterMetaData?.sign_at, "yyyy-MM-dd HH:mm:ss").toUTC().toFormat('yyyy-MM-dd HH:mm:ss') : DateTime.now().toUTC().toFormat('yyyy-MM-dd HH:mm:ss')
          });
        }
        break;
      case 'submit':
        if (!filterMetaData || Object.keys(filterMetaData).length === 0 || !filterMetaData.is_signed) {
          this.feUtil.message.warning('Please sign your report before submitting!', { nzDuration: 3000 });
        } else if (filterMetaData.is_signed) {
          this.feUtil.modalSvc.confirm({
            nzTitle: 'Are you sure you want to submit the report?',
            nzContent: 'This action cannot be undone.',
            nzOkText: 'Submit',
            nzCancelText: 'Cancel',
            nzOnOk: () => {
              Object.assign(payload, {
                is_saved: true,
                is_signed: true,
                is_submit: true,
                saved_at: filterMetaData?.saved_at ? DateTime.fromFormat(filterMetaData?.saved_at, "yyyy-MM-dd HH:mm:ss").toUTC().toFormat('yyyy-MM-dd HH:mm:ss') : DateTime.now().toUTC().toFormat('yyyy-MM-dd HH:mm:ss'),
                sign_at: filterMetaData?.sign_at ? DateTime.fromFormat(filterMetaData?.sign_at, "yyyy-MM-dd HH:mm:ss").toUTC().toFormat('yyyy-MM-dd HH:mm:ss') : DateTime.now().toUTC().toFormat('yyyy-MM-dd HH:mm:ss'),
                submit_at: filterMetaData?.submit_at ? DateTime.fromFormat(filterMetaData?.submit_at, "yyyy-MM-dd HH:mm:ss").toUTC().toFormat('yyyy-MM-dd HH:mm:ss') : DateTime.now().toUTC().toFormat('yyyy-MM-dd HH:mm:ss'),
                report_name: filterMetaData?.report_name
              })
              this.signAndSubmitObservable(payload).pipe(take(1)).subscribe((res) => handleResponse(res, 'Report submitted successfully!'));
            }
          });
        }
        break;
      case 'download':
        if (!filterMetaData || Object.keys(filterMetaData).length === 0 || !filterMetaData.is_signed) {
          this.feUtil.message.warning('The report must be signed before it can be downloaded!');
        } else {
          downloadReport(filterMetaData);
        }
        break;
    }
  }

  openPassAndRemarksModal() {
    let modal: NzModalRef<GenericConfirmationDialogComponent> = this.feUtil.modalSvc.create<GenericConfirmationDialogComponent, TYPE.GenericConfirmationDialog>({
      nzTitle: 'Verify password and add remarks!',
      nzContent: GenericConfirmationDialogComponent,
      nzData: {
        URL: null,
        displayInput: ['password', 'remarks']
      },
      nzCentered: true,
      nzMaskClosable: false,
      nzClosable: false,
      nzCloseOnNavigation: true,
    })
    return modal;
  }

  signAndSubmitObservable(payload: any): Observable<any> {
    return this.signAndSubmitReport(payload)
  }

  getReportType(reportName: string) {
    let reportType = '';
    switch (reportName) {
      case 'Audit Log':
        reportType = TYPE.ReportType.audit;
        break;
      case 'Device Report':
        reportType = TYPE.ReportType.device;
        break;
      case 'Asset Summary':
        reportType = TYPE.ReportType.asset;
        break;
      case 'Incident Summary Report':
        reportType = TYPE.ReportType.incident;
        break;
      case 'Daily Summary Report':
        reportType = TYPE.ReportType.daily;
        break;
    }
    return reportType
  }

  constructor(private readonly feUtil: FeUtil, private readonly uiSvc: UiService) {
    this.loadDefaults();
  }
}